import React from 'react';
import { RegistryClient, RegistryLobbyist } from '../../types/types';
import { color, font, space } from '../../theme';
import { Badge } from '../Badge';
import Icon from '../Icon';
import { firmLabel, inParty, partyDisplayName, SOURCE_NOTE } from './registry';

/* Small pieces of the lobbyist register's UI, shared by the lobbyist page, the lobbyists tab
   and the client view in search. */

// A firm, in the stroke style of components/Icon (which has no briefcase)
export const BriefcaseIcon = ({ size = 20, strokeWidth = 1.8 }: { size?: number; strokeWidth?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3 12.5h18" />
  </svg>
);

// A green badge with a check mark (Badge takes text only)
const CheckBadge = ({ children }: { children: React.ReactNode }) => (
  <Badge tone="success">
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs }}>
      <Icon name="check" size={12} strokeWidth={2.6} />{children}
    </span>
  </Badge>
);

// Lobbyist page header: "registered", then the permit
export const RegistryBadges = ({ entry }: { entry: RegistryLobbyist }) => (
  <>
    <CheckBadge>רשום/ה בפנקס השדלנים</CheckBadge>
    {entry.permitType && <Badge>היתר {entry.permitType}</Badge>}
  </>
);

// A list row: "registered · permanent"
export const RegistryRowBadge = ({ entry }: { entry: RegistryLobbyist }) => (
  <CheckBadge>{entry.permitType ? `בפנקס · ${entry.permitType}` : 'בפנקס'}</CheckBadge>
);

// Under the name: the firm (or "independent"), and the declared party on a line of its own
// (firm names are long, so one shared line would wrap with a stray separator)
export const RegisterLine = ({ entry }: { entry: RegistryLobbyist }) => {
  const firm = firmLabel(entry);
  const party = partyDisplayName(entry.party);
  if (!firm && !party) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xxs, fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>
      {firm && <span>{firm}</span>}
      {party && (
        <span>חבר/ה {inParty(party)} <span style={{ fontSize: font.caption, color: color.textTertiary }}>(הצהרה בפנקס)</span></span>
      )}
    </div>
  );
};

// How the lobbyist represents a client
export const ClientKindBadge = ({ kind }: { kind: RegistryClient['kind'] }) => {
  if (kind === 'זמני') return <Badge tone="warning">ייצוג זמני</Badge>;
  if (kind === 'קבוע') return <Badge>ייצוג קבוע</Badge>;
  return null;
};

export const RegistrySource = () => (
  <p style={{ margin: 0, fontSize: font.caption, color: color.textTertiary, lineHeight: 1.5 }}>{SOURCE_NOTE}</p>
);
